import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Wallet, Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";

export default function LoginPage() {
  const { user, setUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [login, setLogin] = useState({ email: "", password: "" });
  const [reg, setReg] = useState({ name: "", email: "", password: "" });
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const handleForgot = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: forgotEmail.trim() });
      toast.success(data.message);
      setForgotOpen(false);
      setForgotEmail("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setForgotLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", login);
      setUser(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/register", reg);
      setUser(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md fade-in-up">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
            <Wallet className="w-6 h-6 text-primary-foreground" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight">Usta Hesap</h1>
            <p className="text-sm text-muted-foreground">Kolay gelir-gider takibi</p>
          </div>
        </div>

        <div className="bg-card border border-border rounded-2xl p-6 sm:p-8">
          <Tabs defaultValue="login" onValueChange={() => setError("")}>
            <TabsList className="grid grid-cols-2 w-full mb-6 bg-secondary">
              <TabsTrigger value="login" data-testid="login-tab">Giriş Yap</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Kayıt Ol</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">E-posta</Label>
                  <Input id="login-email" data-testid="login-email-input" type="email" required placeholder="ornek@mail.com"
                    value={login.email} onChange={(e) => setLogin({ ...login, email: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password">Şifre</Label>
                  <Input id="login-password" data-testid="login-password-input" type="password" required placeholder="••••••••"
                    value={login.password} onChange={(e) => setLogin({ ...login, password: e.target.value })} />
                </div>
                {error && <p data-testid="auth-error-message" className="text-sm text-destructive">{error}</p>}
                <div className="flex justify-end">
                  <button type="button" data-testid="forgot-password-link" onClick={() => setForgotOpen(true)}
                    className="text-xs text-muted-foreground hover:text-primary transition-colors">
                    Şifremi unuttum
                  </button>
                </div>
                <Button data-testid="login-submit-button" type="submit" disabled={loading}
                  className="w-full rounded-full font-semibold">
                  {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Giriş Yap
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reg-name">Ad Soyad</Label>
                  <Input id="reg-name" data-testid="register-name-input" required placeholder="Adınız Soyadınız"
                    value={reg.name} onChange={(e) => setReg({ ...reg, name: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reg-email">E-posta</Label>
                  <Input id="reg-email" data-testid="register-email-input" type="email" required placeholder="ornek@mail.com"
                    value={reg.email} onChange={(e) => setReg({ ...reg, email: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reg-password">Şifre (en az 6 karakter)</Label>
                  <Input id="reg-password" data-testid="register-password-input" type="password" required minLength={6} placeholder="••••••••"
                    value={reg.password} onChange={(e) => setReg({ ...reg, password: e.target.value })} />
                </div>
                {error && <p data-testid="auth-error-message" className="text-sm text-destructive">{error}</p>}
                <Button data-testid="register-submit-button" type="submit" disabled={loading}
                  className="w-full rounded-full font-semibold">
                  {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Kayıt Ol
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">veya</span>
            </div>
          </div>

          <Button data-testid="google-login-button" variant="outline" onClick={handleGoogle}
            className="w-full rounded-full border-border hover:bg-secondary">
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A11 11 0 0 0 1 12c0 1.77.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Google ile Giriş Yap
          </Button>
        </div>

        <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
          <DialogContent data-testid="forgot-password-dialog" className="bg-card border-border sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <KeyRound className="w-5 h-5 text-primary" /> Şifre Sıfırlama
              </DialogTitle>
              <DialogDescription>
                Kayıtlı e-posta adresinizi girin, size şifre sıfırlama bağlantısı gönderelim.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleForgot} className="space-y-4">
              <Input data-testid="forgot-email-input" type="email" required placeholder="ornek@mail.com" autoFocus
                value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} />
              <div className="flex gap-2 justify-end">
                <Button type="button" data-testid="forgot-cancel-button" variant="ghost" onClick={() => setForgotOpen(false)}
                  className="rounded-full text-muted-foreground">Vazgeç</Button>
                <Button type="submit" data-testid="forgot-submit-button" disabled={forgotLoading || !forgotEmail.trim()}
                  className="rounded-full font-semibold">
                  {forgotLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Bağlantı Gönder
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
